import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  styled,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { motion } from "framer-motion";
import { AppDispatch, RootState } from "../../stores";
import {
  getPastGeneratedReports,
  updateReportNameById,
  ReportResponse,
} from "../../features/generate-report-module/reportAction";

import {
  downloadReport,
  ReportDetails,
  WhiteLabelConfig,
} from "../../utils/downloadReport";

import { getMyAddWhiteLabel } from "../../features/add-white-label-module/addWhiteLabelAction";

// Import fleet report actions and types
import {
  getPastFleetReports,
  updateFleetReportNameById,
} from "../../features/fleet-report-module/fleetReportAction";
import {
  downloadFleetReport,
  FleetReportDetails,
} from "../../utils/downloadFleetReport";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "auto",
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(1.2),
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(75, 91, 93, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.65rem",
    padding: theme.spacing(1),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  padding: theme.spacing(1.2, 0.5),
  lineHeight: "1.2rem",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    backgroundColor: "#ede7d9",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: "#4b5b5d",
  fontSize: "0.875rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: 0,
  },
}));

const DownloadSelect = styled(Select)(({ theme }) => ({
  fontSize: "0.75rem",
  height: "24px",
  "& .MuiSelect-select": {
    padding: "4px 8px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.65rem",
    height: "20px",
    "& .MuiSelect-select": {
      padding: "2px 6px",
    },
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "200px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

// Use ReportResponse directly to avoid duplication
type Report = ReportResponse;

const ReportTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();

  // Site Estimator Reports state
  const { reports, loading, error } = useSelector(
    (state: RootState) => state.report
  );

  // Fleet Estimator Reports state
  const {
    reports: fleetReports,
    loading: fleetLoading,
    error: fleetError,
  } = useSelector((state: RootState) => state.fleetReport);

  const { whiteLabel, loading: whiteLabelLoading } = useSelector(
    (state: RootState) => state.addWhiteLabel
  );

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const currentPlanName = (
    userData?.subscription?.planName || ""
  ).toLowerCase();
  const isStarterPlan = currentPlanName === "core";

  // Site Estimator state
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [editingReportName, setEditingReportName] = useState("");

  // Fleet Estimator state
  const [editingFleetReportId, setEditingFleetReportId] = useState<
    string | null
  >(null);
  const [editingFleetReportName, setEditingFleetReportName] = useState("");

  // Common state
  const [downloadType, setDownloadType] = useState<{ [key: string]: string }>(
    {}
  );
  const [fleetDownloadType, setFleetDownloadType] = useState<{
    [key: string]: string;
  }>({});
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  const page = 1;
  const pageSize = 5;
  const fleetPage = 1;
  const fleetPageSize = 5;

  useEffect(() => {
    if (tabValue === 0) {
      // Load Site Estimator Reports
      dispatch(getPastGeneratedReports({ page, limit: pageSize }));
    } else {
      // Load Fleet Estimator Reports
      dispatch(getPastFleetReports({ page: fleetPage, limit: fleetPageSize }));
    }
  }, [dispatch, page, pageSize, fleetPage, fleetPageSize, tabValue]);

  useEffect(() => {
    const hasWLDownload =
      Object.values(downloadType).includes("WL") ||
      Object.values(fleetDownloadType).includes("WL");
    if (hasWLDownload && !whiteLabel && !whiteLabelLoading) {
      dispatch(getMyAddWhiteLabel());
    }
  }, [
    dispatch,
    downloadType,
    fleetDownloadType,
    whiteLabel,
    whiteLabelLoading,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setEditingReportId(null);
    setEditingFleetReportId(null);
  };

  // Site Estimator editing
  const startEditing = (reportId: string, currentName: string) => {
    if (loading) return;
    setEditingReportId(reportId);
    setEditingReportName(currentName);
  };

  const cancelEditing = () => {
    setEditingReportId(null);
    setEditingReportName("");
  };

  const saveEditing = async () => {
    if (!editingReportId) return;
    const trimmed = editingReportName.trim();
    const original = reports.find((r) => r._id === editingReportId)?.reportName;
    if (!trimmed || trimmed === original) {
      cancelEditing();
      return;
    }
    try {
      await dispatch(
        updateReportNameById({ id: editingReportId, reportName: trimmed })
      );
    } finally {
      cancelEditing();
    }
  };

  // Fleet Estimator editing
  const startFleetEditing = (reportId: string, currentName: string) => {
    if (fleetLoading) return;
    setEditingFleetReportId(reportId);
    setEditingFleetReportName(currentName);
  };

  const cancelFleetEditing = () => {
    setEditingFleetReportId(null);
    setEditingFleetReportName("");
  };

  const saveFleetEditing = async () => {
    if (!editingFleetReportId) return;
    const trimmed = editingFleetReportName.trim();
    const original = fleetReports.find(
      (r) => r._id === editingFleetReportId
    )?.reportName;

    if (!trimmed) {
      alert("Report name cannot be empty");
      return;
    }

    if (trimmed === original) {
      cancelFleetEditing();
      return;
    }

    try {
      await dispatch(
        updateFleetReportNameById({
          id: editingFleetReportId,
          reportName: trimmed,
        })
      ).unwrap();
      cancelFleetEditing();
    } catch (error) {
      console.error("Failed to update fleet report name:", error);
    }
  };

  // Handle key events for fleet report editing
  const handleFleetEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveFleetEditing();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelFleetEditing();
    }
  };

  const handleDownloadPdf = async (report: any, downloadType: string) => {
    if (!report) {
      alert("No report data to download.");
      return;
    }

    const isWhiteLabel = downloadType === "WL";
    let whiteLabelConfig: WhiteLabelConfig | undefined;
    if (isWhiteLabel && whiteLabel) {
      whiteLabelConfig = {
        brandLogo: whiteLabel.brandLogo,
        brandColor: whiteLabel.brandColor,
        textColor: whiteLabel.textColor,
        bgColor: whiteLabel.bgColor,
        domainURL: whiteLabel.domainURL || "",
        termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
        privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
      };
    }

    try {
      setDownloadingPdf(report._id);

      // For Site Estimator Reports
      if (tabValue === 0) {
        const details: ReportDetails = {
          reportName: report.reportName || "Charging Station",
          netProfit: report.netProfit || 0,
          address: report.location || "",
          financialTable: report.financialTable || [],
          breakEvenCashFlow: report.breakEvenCashFlow || {
            labels: ["0", "1", "2", "3", "4", "5"],
            data: Array(6).fill(0),
          },
          revenueCost: report.revenueCost || {
            labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
            datasets: [],
          },
          co2Savings: report.co2Savings || {
            labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
            datasets: [],
          },
          utilityDetails: {
            state: report.utilityDetails?.state || "--",
            zipCode: report.utilityDetails?.zipCode || "--",
            utilityName: report.utilityDetails?.utilityName || "--",
            utilityId: report.utilityDetails?.utilityId || "--",
            scheduleId: report.utilityDetails?.scheduleId || "--",
            totalCost: Number(report.utilityDetails?.totalCost) || 0,
          },
          energyCosts: report.energyCosts || {
            energy: Array(5).fill(0),
            energyTime: Array(5).fill(0),
            incrementalEnergy: Array(5).fill(0),
            demand: Array(5).fill(0),
            demandTime: Array(5).fill(0),
            incrementalDemand: Array(5).fill(0),
            serviceCharge: Array(5).fill(0),
            otherCharges: Array(5).fill(0),
            total: Array(5).fill(0),
          },
          grantCostDetails: report.grantCostDetails || [],
          excludeIncentives: report.excludeIncentives || false,
          taxBenefits: {
            applyTaxBenefits: report.applyTaxBenefits || false,
            businessTaxRate: report.businessTaxRate || 21,
            section179Limit: report.section179Limit || 0,
            bonusDepreciationPct: report.bonusDepreciationPct || 0,
            qualifyingSpend: report.qualifyingSpend || 0,
            taxSavings: report.taxSavings || 0,
            upfrontAfterTax: report.upfrontAfterTax || 0,
          },
        };

        await downloadReport(details, isWhiteLabel, whiteLabelConfig);
      } else {
        // For Fleet Estimator Reports
        const details: FleetReportDetails = {
          reportName: report.reportName,
          netSavings: report.netSavings,
          annualSavings: report.annualSavings || 0,
          address: report.location || report.address || "",
          city: report.city || "",
          zipCode: report.zipCode || "",
          fleetSize: report.fleetSize || parseInt(report.numVehicles || "1"),
          analysisYears: report.analysisYears || parseInt(report.years || "7"),
          paybackPeriod: report.paybackPeriod || 0,
          financialTable: report.financialTable || [
            {
              category: "Annual ICE Fuel Cost",
              value: `$${report.iceFuelCostAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual EV Energy Cost",
              value: `$${report.evEnergyCostAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual ICE Maintenance",
              value: `$${report.iceMaintAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual EV Maintenance",
              value: `$${report.evMaintAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual ICE Opex",
              value: `$${report.iceOpexAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual EV Opex",
              value: `$${report.evOpexAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual Savings",
              value: `$${report.annualSavings?.toLocaleString() || "0"}`,
            },
            {
              category: "Upfront EVs Cost",
              value: `$${report.upfrontEVs?.toLocaleString() || "0"}`,
            },
            {
              category: "Incentives Total",
              value: `$${report.incentives?.toLocaleString() || "0"}`,
            },
            {
              category: "Resale Value Total",
              value: `$${report.resaleTotal?.toLocaleString() || "0"}`,
            },
            {
              category: "Upfront Gross",
              value: `$${report.upfrontGross?.toLocaleString() || "0"}`,
            },
            {
              category: "Upfront Net Outlay",
              value: `$${report.upfrontNet?.toLocaleString() || "0"}`,
            },
          ],
          taxBenefitsTable:
            report.taxBenefitsTable ||
            (report.applyTaxBenefits
              ? [
                  {
                    category: "Business Tax Rate",
                    value: `${report.businessTaxRate || 21}%`,
                  },
                  {
                    category: "Section 179 Limit",
                    value: `$${(
                      report.section179Limit || 2500000
                    ).toLocaleString()}`,
                  },
                  {
                    category: "Bonus Depreciation %",
                    value: `${report.bonusDepreciationPct || 0}%`,
                  },
                  {
                    category: "Qualifying Spend",
                    value: `$${(report.qualifyingSpend || 0).toLocaleString()}`,
                  },
                  {
                    category: "Est. Section 179 Deduction",
                    value: `$${(report.est179Deduction || 0).toLocaleString()}`,
                  },
                  {
                    category: "Est. 168(k) Deduction",
                    value: `$${(
                      report.est168kDeduction || 0
                    ).toLocaleString()}`,
                  },
                  {
                    category: "Estimated Tax Savings",
                    value: `$${(report.estTaxSavings || 0).toLocaleString()}`,
                  },
                ]
              : undefined),
          environmentalTable: report.environmentalTable || [
            {
              category: "Annual Gasoline Avoided (gal)",
              value: `${report.gallonsAvoidedAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual CO₂ Offset (kg, tailpipe)",
              value: `${report.co2AnnualKgTailpipe?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual Grid Emissions (kg)",
              value: `${report.gridEmissionsAnnual?.toLocaleString() || "0"}`,
            },
            {
              category: "Annual Net CO₂ (kg)",
              value: `${report.co2AnnualKgNet?.toLocaleString() || "0"}`,
            },
            {
              category: `${report.analysisYears || 7}-Year Net CO₂ (tons)`,
              value: `${(report.co2NetTons || 0).toFixed(2)}`,
            },
          ],
          costOverTimeData: report.costOverTimeData,
          cumulativeSavingsData: report.cumulativeSavingsData,
          environmentalData: report.environmentalData,
          utilityDetails: report.utilityDetails,
          utilityRate: report.utilityRate || 0.14,
          applyTaxBenefits: report.applyTaxBenefits || false,
        };

        await downloadFleetReport(
          details,
          isWhiteLabel,
          whiteLabelConfig,
          false
        );
      }
    } catch (err) {
      console.error("PDF generation failed", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadTypeChange = (reportId: string, value: string) => {
    if (tabValue === 0) {
      setDownloadType((prev) => ({
        ...prev,
        [reportId]: value,
      }));
    } else {
      setFleetDownloadType((prev) => ({
        ...prev,
        [reportId]: value,
      }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
        }}
      >
        <Header>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight={700}
            color="#4b5b5d"
          >
            Recent Reports
          </Typography>
        </Header>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: "#4b5b5d",
                fontWeight: 500,
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#455354",
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#455354",
              },
            }}
          >
            <Tab label="Site Estimator Report" />
            <Tab label="Fleet Estimator Report" />
          </Tabs>
        </Box>

        {/* Site Estimator Reports Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <LoaderContainer>
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: "#4b5b5d",
                }}
              />
              <LoaderText>Loading site estimator reports...</LoaderText>
            </LoaderContainer>
          ) : error ? (
            <Typography color="error" variant="h6" align="center">
              {error}
            </Typography>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                "-webkit-overflow-scrolling": "touch",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#4b5b5d",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#354142",
                },
              }}
            >
              <StyledTable size={isMobile ? "small" : "medium"}>
                <TableHead sx={{ background: "#f5f0e7" }}>
                  <TableRow>
                    <StyledTableHeadCell>Report Name</StyledTableHeadCell>
                    <StyledTableHeadCell>Location</StyledTableHeadCell>
                    <StyledTableHeadCell>Date Generated</StyledTableHeadCell>
                    <StyledTableHeadCell>Net Profit</StyledTableHeadCell>
                    <StyledTableHeadCell>Download</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow>
                      <StyledTableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography
                          variant="h6"
                          color="#4b5b5d"
                          fontWeight={500}
                        >
                          No Data Available
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    reports.map((report) => {
                      const truncatedReportName =
                        report.reportName.length > 25
                          ? `${report.reportName.slice(0, 25)}...`
                          : report.reportName;
                      const truncatedLocation =
                        report.location.length > 20
                          ? `${report.location.slice(0, 20)}...`
                          : report.location;

                      return (
                        <TableRow
                          key={report._id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f8f8f8",
                            },
                            "&:hover": { backgroundColor: "#e0e0e0" },
                          }}
                        >
                          <StyledTableCell
                            onDoubleClick={() =>
                              startEditing(report._id, report.reportName)
                            }
                            sx={{ cursor: "text" }}
                          >
                            {editingReportId === report._id ? (
                              <TextField
                                value={editingReportName}
                                size={isMobile ? "small" : "medium"}
                                autoFocus
                                onChange={(e) =>
                                  setEditingReportName(e.target.value)
                                }
                                sx={{
                                  "& .MuiOutlinedInput-input": {
                                    padding: "4px 8px",
                                    fontSize: "0.8rem",
                                  },
                                }}
                                onBlur={saveEditing}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveEditing();
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEditing();
                                  }
                                }}
                                variant="outlined"
                                fullWidth
                              />
                            ) : (
                              <Tooltip
                                title={report.reportName}
                                placement="top"
                              >
                                <span>{truncatedReportName}</span>
                              </Tooltip>
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Tooltip title={report.location} placement="top">
                              <span>{truncatedLocation}</span>
                            </Tooltip>
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatDate(report.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell>
                            ${report.netProfit?.toLocaleString() || "0"}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexDirection: isMobile ? "column" : "row",
                                alignItems: "center",
                              }}
                            >
                              <DownloadSelect
                                value={downloadType[report._id] || "PDF"}
                                onChange={(e) =>
                                  handleDownloadTypeChange(
                                    report._id,
                                    e.target.value as string
                                  )
                                }
                                disabled={
                                  loading || downloadingPdf === report._id
                                }
                              >
                                <MenuItem
                                  value="PDF"
                                  sx={{ fontSize: "0.65rem" }}
                                >
                                  PDF
                                </MenuItem>
                                {!isStarterPlan && (
                                  <MenuItem
                                    value="WL"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    WL
                                  </MenuItem>
                                )}
                              </DownloadSelect>
                              <Tooltip title="Download Report">
                                <DownloadButton
                                  variant="text"
                                  onClick={() =>
                                    handleDownloadPdf(
                                      report,
                                      downloadType[report._id] || "PDF"
                                    )
                                  }
                                  startIcon={
                                    downloadingPdf === report._id ? (
                                      <CircularProgress
                                        size={16}
                                        sx={{ color: "#4b5b5d" }}
                                      />
                                    ) : (
                                      <ArrowDownwardIcon
                                        fontSize={isMobile ? "small" : "medium"}
                                      />
                                    )
                                  }
                                  disabled={
                                    loading || downloadingPdf === report._id
                                  }
                                >
                                  {!isMobile && "Download"}
                                </DownloadButton>
                              </Tooltip>
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </StyledTable>
            </Box>
          )}
        </TabPanel>

        {/* Fleet Estimator Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          {fleetLoading ? (
            <LoaderContainer>
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: "#4b5b5d",
                }}
              />
              <LoaderText>Loading fleet estimator reports...</LoaderText>
            </LoaderContainer>
          ) : fleetError ? (
            <Typography color="error" variant="h6" align="center">
              {fleetError}
            </Typography>
          ) : (
            <Box
              sx={{
                overflowX: "auto",
                "-webkit-overflow-scrolling": "touch",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#4b5b5d",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#354142",
                },
              }}
            >
              <StyledTable size={isMobile ? "small" : "medium"}>
                <TableHead sx={{ background: "#f5f0e7" }}>
                  <TableRow>
                    <StyledTableHeadCell>Report Name</StyledTableHeadCell>
                    <StyledTableHeadCell>Location</StyledTableHeadCell>
                    <StyledTableHeadCell>Date Generated</StyledTableHeadCell>
                    <StyledTableHeadCell>Net Savings</StyledTableHeadCell>
                    <StyledTableHeadCell>Download</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fleetReports.length === 0 ? (
                    <TableRow>
                      <StyledTableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography
                          variant="h6"
                          color="#4b5b5d"
                          fontWeight={500}
                        >
                          No Data Available
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    fleetReports.map((report) => {
                      const truncatedReportName =
                        report.reportName.length > 25
                          ? `${report.reportName.slice(0, 25)}...`
                          : report.reportName;
                      const truncatedLocation =
                        (report.location || "").length > 20
                          ? `${(report.location || "").slice(0, 20)}...`
                          : report.location || "";

                      return (
                        <TableRow
                          key={report._id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f8f8f8",
                            },
                            "&:hover": { backgroundColor: "#e0e0e0" },
                          }}
                        >
                          <StyledTableCell
                            onDoubleClick={() =>
                              startFleetEditing(report._id, report.reportName)
                            }
                            sx={{ cursor: "text" }}
                          >
                            {editingFleetReportId === report._id ? (
                              <TextField
                                value={editingFleetReportName}
                                size={isMobile ? "small" : "medium"}
                                autoFocus
                                onChange={(e) =>
                                  setEditingFleetReportName(e.target.value)
                                }
                                onBlur={saveFleetEditing}
                                onKeyDown={handleFleetEditKeyDown}
                                variant="outlined"
                                fullWidth
                                sx={{
                                  "& .MuiOutlinedInput-input": {
                                    padding: "4px 8px",
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            ) : (
                              <Tooltip
                                title={report.reportName}
                                placement="top"
                              >
                                <span>{truncatedReportName}</span>
                              </Tooltip>
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Tooltip title={report.location} placement="top">
                              <span>{truncatedLocation}</span>
                            </Tooltip>
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatDate(report.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell>
                            ${report.netSavings?.toLocaleString() || "0"}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexDirection: isMobile ? "column" : "row",
                                alignItems: "center",
                              }}
                            >
                              <DownloadSelect
                                value={fleetDownloadType[report._id] || "PDF"}
                                onChange={(e) =>
                                  handleDownloadTypeChange(
                                    report._id,
                                    e.target.value as string
                                  )
                                }
                                disabled={
                                  fleetLoading || downloadingPdf === report._id
                                }
                              >
                                <MenuItem
                                  value="PDF"
                                  sx={{ fontSize: "0.65rem" }}
                                >
                                  PDF
                                </MenuItem>
                                {!isStarterPlan && (
                                  <MenuItem
                                    value="WL"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    WL
                                  </MenuItem>
                                )}
                              </DownloadSelect>
                              <Tooltip title="Download Report">
                                <DownloadButton
                                  variant="text"
                                  onClick={() =>
                                    handleDownloadPdf(
                                      report,
                                      fleetDownloadType[report._id] || "PDF"
                                    )
                                  }
                                  startIcon={
                                    downloadingPdf === report._id ? (
                                      <CircularProgress
                                        size={16}
                                        sx={{ color: "#4b5b5d" }}
                                      />
                                    ) : (
                                      <ArrowDownwardIcon
                                        fontSize={isMobile ? "small" : "medium"}
                                      />
                                    )
                                  }
                                  disabled={
                                    fleetLoading ||
                                    downloadingPdf === report._id
                                  }
                                >
                                  {!isMobile && "Download"}
                                </DownloadButton>
                              </Tooltip>
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </StyledTable>
            </Box>
          )}
        </TabPanel>
      </motion.div>
    </Container>
  );
};

export default ReportTable;
